import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { map } from 'rxjs';
import { BalancePaymentAmount, ISpecialPayment, SpecialPaymentStatus } from 'core/types/balance-transaction.interface';
import { TagComponent, TagComponentType } from 'shared/components/tag/tag.component';
import { formatBalanceDate, formatBalanceMoney } from 'shared/utils/balance-format.utils';

const EMPTY_VALUE = '—';

@Component({
  selector: 'app-special-payments',
  standalone: true,
  imports: [TranslateModule, TagComponent],
  templateUrl: './special-payments.component.html',
  styleUrl: './special-payments.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SpecialPaymentsComponent {
  private readonly _router = inject(Router);
  private readonly _translateService = inject(TranslateService);

  readonly specialPayments = input.required<ISpecialPayment[]>();
  readonly language = toSignal(this._translateService.onLangChange.pipe(map(event => event.lang)), {
    initialValue: this._translateService.getCurrentLang() || 'es'
  });
  readonly paymentRows = computed(() =>
    this.specialPayments().map(payment => {
      const adjustment = Number(payment.adjustment_amount);
      return {
        payment,
        dateLabel: this.formatDate(payment.payment_date),
        periodLabel: this.formatPeriod(payment.period),
        amountLabel: this.formatAmount(payment.amount),
        adjustmentLabel: Number.isFinite(adjustment) && adjustment > 0 ? this.formatAmount(-adjustment) : null,
        hasDetails: this.canOpenPaymentDetails(payment),
        tagType: this.getStatusTagType(payment.status),
        statusTranslationKey: this.getStatusTranslationKey(payment.status)
      };
    })
  );

  // Adjustment and Carried months have null payment dates.
  formatDate(date: string | null): string {
    return formatBalanceDate(date, this.language()) || EMPTY_VALUE;
  }

  formatPeriod(month: string): string {
    return formatBalanceDate(month, this.language(), 'period') || EMPTY_VALUE;
  }

  isCarried(payment: ISpecialPayment): boolean {
    return payment.status === 'Carried';
  }

  canOpenPaymentDetails(payment: ISpecialPayment): boolean {
    return (
      Number.isSafeInteger(payment.id) &&
      payment.id > 0 &&
      (!this.isCarried(payment) || payment.adjustment_amount !== undefined)
    );
  }

  getStatusTagType(status: SpecialPaymentStatus): TagComponentType {
    switch (status) {
      case 'Paid':
        return 'brand';
      case 'Pending':
      case 'Held':
        return 'warning';
      case 'Overdue':
      case 'Failed':
        return 'error';
      case 'Carried':
        return 'info';
      case 'Adjustment':
        return 'neutral';
    }
  }

  getStatusTranslationKey(status: SpecialPaymentStatus): string {
    return `balance.${status.toLowerCase()}`;
  }

  goToPaymentDetails(payment: ISpecialPayment): void {
    if (!this.canOpenPaymentDetails(payment)) return;
    void this._router.navigate(['workspace', 'balance', 'payments', payment.id]);
  }

  private formatAmount(value: BalancePaymentAmount): string {
    return formatBalanceMoney(value);
  }
}
